import { Button, Select, Tooltip } from '@meitu/saas-ui';
import { useTemplateModel } from '@/store/models/template-model';
import './ppt-theme-btn.less';
import { useEffect, useState } from 'react';
import message from '@meitu/saas-ui/es/message';
import { IFont } from '@meitu/saas-editor-core/es/version-types';
import { AutoDragModal } from '@meitu/saas-editor-ui/es/panels/image-layer-panel/components/effect-panel/views/auto-drag-model/auto-drag-model';
import IconFont from '@meitu/xdesign-icon-font';
import { ColorGroup } from '@meitu/xdesign-widgets';
import { type IEditor } from '@/instance';

interface IColorItem {
    mainThemeColor: string;
    subThemeColor: string;
    thereThemeColor: string;
}
export interface IThemeInfo {
    titleFontName: string;
    titleFontId: string;
    subTitleFontName: string;
    subTitleFontId: string;
    numberFontName: string;
    numberFontId: string;
    darkColors: IColorItem[];
    lightColors: IColorItem[];
}

const noIncludeFont = [
    'AlibabaPuHuiTi-Bold',
    'AlibabaPuHuiTi-Light',
    'AlibabaPuHuiTi-Medium',
    'AlibabaPuHuiTi-Regular',
    'AlibabaPuHuiTi-Heavy',
    'D-DIN-PRO-Heavy',
    'D-DIN-PRO-Bold',
    'D-DIN-PRO-SemiBold',
    'D-DIN-PRO-Regular',
    'AlibabaSans-Lightltalic',
    'AlibabaSans-Regular',
    'AlibabaSans-Medium',
    'AlibabaSans-Black',
    'GamaHand',
    'Daizen',
    'AlibabaSans-Light',
    'AlibabaSans-Italic',
    'AlibabaSans-Bold',
    'Kallem',
    'AlibabaSans-MediumItalic',
    'NewYork',
    'BMHANNAPro',
    'BMDoHyeon',
];
const filterFonts = (fonts: any[]) => {
    return fonts.filter((font) => !noIncludeFont.includes(font.font_name));
};

const DefaultThemeInfo: IThemeInfo = {
    titleFontName: '',
    titleFontId: '',
    subTitleFontName: '',
    subTitleFontId: '',
    numberFontName: '',
    numberFontId: '',
    darkColors: [
        {
            mainThemeColor: '#e7e7e7',
            subThemeColor: '#e7e7e7',
            thereThemeColor: '#e7e7e7',
        },
        {
            mainThemeColor: '#e7e7e7',
            subThemeColor: '#e7e7e7',
            thereThemeColor: '#e7e7e7',
        },
        {
            mainThemeColor: '#e7e7e7',
            subThemeColor: '#e7e7e7',
            thereThemeColor: '#e7e7e7',
        },
        {
            mainThemeColor: '#e7e7e7',
            subThemeColor: '#e7e7e7',
            thereThemeColor: '#e7e7e7',
        },
        {
            mainThemeColor: '#e7e7e7',
            subThemeColor: '#e7e7e7',
            thereThemeColor: '#e7e7e7',
        },
        {
            mainThemeColor: '#e7e7e7',
            subThemeColor: '#e7e7e7',
            thereThemeColor: '#e7e7e7',
        },
    ],
    lightColors: [
        {
            mainThemeColor: '#e7e7e7',
            subThemeColor: '#e7e7e7',
            thereThemeColor: '#e7e7e7',
        },
        {
            mainThemeColor: '#e7e7e7',
            subThemeColor: '#e7e7e7',
            thereThemeColor: '#e7e7e7',
        },
        {
            mainThemeColor: '#e7e7e7',
            subThemeColor: '#e7e7e7',
            thereThemeColor: '#e7e7e7',
        },
        {
            mainThemeColor: '#e7e7e7',
            subThemeColor: '#e7e7e7',
            thereThemeColor: '#e7e7e7',
        },
        {
            mainThemeColor: '#e7e7e7',
            subThemeColor: '#e7e7e7',
            thereThemeColor: '#e7e7e7',
        },
        {
            mainThemeColor: '#e7e7e7',
            subThemeColor: '#e7e7e7',
            thereThemeColor: '#e7e7e7',
        },
    ],
};

const hasEmptyString = (obj: { [key: string]: any }) => {
    // 如果是字符串且为空，返回 true
    if (typeof obj === 'string' && obj === '') {
        return true;
    }

    // 如果是对象或数组，递归遍历
    if (typeof obj === 'object' && obj !== null) {
        for (const key in obj) {
            if (hasEmptyString(obj[key])) {
                return true;
            }
        }
    }

    // 如果没有找到空字符串，返回 false
    return false;
};

const PPTThemeModal: React.FC<{
    extraInfo: any;
    setExtraInfo: any;
    fonts: any[];
}> = ({ extraInfo, setExtraInfo, fonts }) => {
    fonts = filterFonts(fonts);
    const [themeInfo, setThemeInfo] = useState<IThemeInfo>(extraInfo?.themeInfo || DefaultThemeInfo);

    const colorList = (
        index: number,
        item: IColorItem,
        themeType: 'darkColors' | 'lightColors',
        themeInfo: IThemeInfo,
    ) => {
        return (
            <div className="color-sub-item" key={`${index}-${themeType}`}>
                <div className="item">颜色{index + 1}</div>
                <div className="item">
                    <ColorGroup
                        placement="bottom"
                        color={item.mainThemeColor}
                        setColor={(val) => {
                            const canReplaceAll = themeInfo[themeType].every((item) => {
                                return item.mainThemeColor === '#e7e7e7';
                            });

                            setThemeInfo({
                                ...themeInfo,
                                [themeType]: themeInfo[themeType].map((item, i) => {
                                    if (canReplaceAll) {
                                        return {
                                            ...item,
                                            mainThemeColor: val,
                                        };
                                    } else if (index === i) {
                                        return {
                                            ...item,
                                            mainThemeColor: val,
                                        };
                                    }
                                    return item;
                                }),
                            });
                        }}
                        customPopupTrigger={
                            <div
                                style={{
                                    width: 25,
                                    height: 25,
                                    backgroundColor: item.mainThemeColor,
                                    borderRadius: 4,
                                    cursor: 'pointer',
                                    border: '1px solid var(--stroke-system-border-overlay, rgba(0, 0, 0, 0.08))',
                                    display: 'block',
                                }}
                            ></div>
                        }
                    />
                </div>
                <div className="item">
                    <ColorGroup
                        placement="bottom"
                        color={item.subThemeColor}
                        setColor={(val) => {
                            const canReplaceAll = themeInfo[themeType].every((item) => {
                                return item.subThemeColor === '#e7e7e7';
                            });

                            setThemeInfo({
                                ...themeInfo,
                                [themeType]: themeInfo[themeType].map((item, i) => {
                                    if (canReplaceAll) {
                                        return {
                                            ...item,
                                            subThemeColor: val,
                                        };
                                    } else if (index === i) {
                                        return {
                                            ...item,
                                            subThemeColor: val,
                                        };
                                    }
                                    return item;
                                }),
                            });
                        }}
                        customPopupTrigger={
                            <div
                                style={{
                                    width: 25,
                                    height: 25,
                                    backgroundColor: item.subThemeColor,
                                    borderRadius: 4,
                                    cursor: 'pointer',
                                    border: '1px solid var(--stroke-system-border-overlay, rgba(0, 0, 0, 0.08))',
                                }}
                            ></div>
                        }
                    />
                </div>
                <div className="item">
                    <ColorGroup
                        placement="bottom"
                        color={item.thereThemeColor}
                        setColor={(val) => {
                            const canReplaceAll = themeInfo[themeType].every((item) => {
                                return item.thereThemeColor === '#e7e7e7';
                            });

                            setThemeInfo({
                                ...themeInfo,
                                [themeType]: themeInfo[themeType].map((item, i) => {
                                    if (canReplaceAll) {
                                        return {
                                            ...item,
                                            thereThemeColor: val,
                                        };
                                    } else if (index === i) {
                                        return {
                                            ...item,
                                            thereThemeColor: val,
                                        };
                                    }
                                    return item;
                                }),
                            });
                        }}
                        customPopupTrigger={
                            <div
                                style={{
                                    width: 25,
                                    height: 25,
                                    backgroundColor: item.thereThemeColor,
                                    borderRadius: 4,
                                    cursor: 'pointer',
                                    border: '1px solid var(--stroke-system-border-overlay, rgba(0, 0, 0, 0.08))',
                                }}
                            ></div>
                        }
                    />
                </div>
            </div>
        );
    };
    const closeClick = () => {
        AutoDragModal.close();
    };

    const applyTheme = () => {
        if (hasEmptyString(themeInfo)) {
            message.error('所以数据都要填不能放空');
            return;
        }
        // 为空不让应用
        let newExtraInfo = {} as any;
        if (extraInfo) {
            newExtraInfo = { ...extraInfo };
        }
        newExtraInfo.themeInfo = themeInfo;
        setExtraInfo(newExtraInfo);
        AutoDragModal.close();
    };

    return (
        <div className="c-ppt-theme-btn">
            <div className="header" data-type="canDrag">
                <div className="title">主题管理</div>
                <div className="operate">
                    <div className="close" onClick={closeClick}>
                        <IconFont name="CrossBlack" fontSize={12} color="var(--content-system-secondary)" />
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="sub-title">颜色管理</div>
                <div className="color-item light-color">
                    <div className="color-sub-item">
                        <div className="item">深色模式</div>
                        <div className="item">主题色：</div>
                        <div className="item">
                            <Tooltip placement="top" title="主题色内容内文本/图标颜色">
                                主题内容色
                                <IconFont name="QuestionMarkCircleBold" />
                            </Tooltip>
                        </div>
                        <div className="item">
                            <Tooltip placement="top" title="常规文本/图标等颜色">
                                中性色
                                <IconFont name="QuestionMarkCircleBold" />
                            </Tooltip>
                        </div>
                    </div>
                    {themeInfo.darkColors.map((item, index) => colorList(index, item, 'darkColors', themeInfo))}
                </div>
                <div className="color-item light-color">
                    <div className="color-sub-item">
                        <div className="item">浅色模式</div>
                        <div className="item">主题色：</div>
                        <div className="item">
                            <Tooltip placement="top" title="主题色内容内文本/图标颜色">
                                主题内容色
                                <IconFont name="QuestionMarkCircleBold" />
                            </Tooltip>
                        </div>
                        <div className="item">
                            <Tooltip placement="top" title="常规文本/图标等颜色">
                                中性色
                                <IconFont name="QuestionMarkCircleBold" />
                            </Tooltip>
                        </div>
                    </div>
                    {themeInfo.lightColors.map((item, index) => colorList(index, item, 'lightColors', themeInfo))}
                </div>
                <div className="sub-title">字体管理</div>
                <div className="font-item">
                    <div className="item">
                        <div className="item-title">列表标题：</div>
                        <div className="item-content">
                            <Select
                                showSearch
                                value={themeInfo.titleFontName}
                                optionFilterProp="label"
                                onChange={(val) => {
                                    setThemeInfo({
                                        ...themeInfo,
                                        titleFontName: val,
                                        titleFontId: fonts.find((font) => font.font_name === val)?.id,
                                    });
                                }}
                                options={fonts.map((font) => ({
                                    label: font.name,
                                    value: font.font_name,
                                }))}
                            ></Select>
                        </div>
                    </div>
                    <div className="item">
                        <div className="item-title">列表正文：</div>
                        <div className="item-content">
                            <Select
                                showSearch
                                optionFilterProp="label"
                                value={themeInfo.subTitleFontName}
                                onChange={(val) => {
                                    setThemeInfo({
                                        ...themeInfo,
                                        subTitleFontName: val,
                                        subTitleFontId: fonts.find((font) => font.font_name === val)?.id,
                                    });
                                }}
                                options={fonts.map((font) => ({
                                    label: font.name,
                                    value: font.font_name,
                                }))}
                            ></Select>
                        </div>
                    </div>
                    <div className="item">
                        <div className="item-title">数字：</div>
                        <div className="item-content">
                            <Select
                                showSearch
                                optionFilterProp="label"
                                value={themeInfo.numberFontName}
                                onChange={(val) => {
                                    setThemeInfo({
                                        ...themeInfo,
                                        numberFontName: val,
                                        numberFontId: fonts.find((font) => font.font_name === val)?.id,
                                    });
                                }}
                                options={fonts.map((font) => ({
                                    label: font.name,
                                    value: font.font_name,
                                }))}
                            ></Select>
                        </div>
                    </div>
                </div>

                <div className="apply-btn">
                    <Button onClick={closeClick}>取消</Button>
                    <Button type="primary" onClick={applyTheme}>
                        应用主题
                    </Button>
                </div>
            </div>
        </div>
    );
};

const PptThemeBtn: React.FC<{
    editor: IEditor;
}> = ({ editor }) => {
    const [fonts, setFontsList] = useState<IFont[]>(editor.services.font.getFonts());
    const extraInfo = useTemplateModel((state) => state.extraInfo);
    const setExtraInfo = useTemplateModel((state) => state.setExtraInfo);

    useEffect(() => {
        const getFonts = () => {
            setTimeout(() => {
                const fonts = editor.services.font.getFonts();
                if (fonts.length === 0) {
                    getFonts();
                } else {
                    setFontsList(fonts);
                }
            }, 1000);
        };

        getFonts();
    }, []);

    const showTheme = () => {
        AutoDragModal.show({
            children: <PPTThemeModal extraInfo={extraInfo} setExtraInfo={setExtraInfo} fonts={fonts} />,
            initPosition: {
                x: document.documentElement.clientWidth - 600,
                y: 60,
            },
            windowSize: {
                width: 400,
                height: 590,
            },
            className: 'c-ppt-theme-btn-wrap',
            bgCanOperate: true,
        });
    };
    return (
        <div style={{ marginRight: '10px' }}>
            <Button type="primary" data-blue onClick={showTheme}>
                主题管理
            </Button>
        </div>
    );
};

export default PptThemeBtn;
